import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CalendarDays, Clock, Loader2, MapPin, Pencil, Plus } from "lucide-react";
import {
  GetUploadedEventsAPI,
  type StudentEvent,
} from "@/services/api/user/events";
import { GetUserSocietiesAPI } from "@/services/api/society/getUserSocieties";
import {
  formatDateRange,
  formatTimeRange,
  localized,
  toImageList,
  toUniversityCodes,
} from "@/utils/eventFormat";
import StudentUniversityLogos from "@/components/student/StudentUniversityLogos";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";

const PAGE_SIZE = 10;

/**
 * Organizer "My events" list — every event uploaded to the societies the
 * student belongs to, each with an Edit entry point into the edit wizard.
 */
const StudentMyEventsPage: React.FC = () => {
  const { t } = useTranslation();
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );

  const [events, setEvents] = useState<StudentEvent[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [hasSociety, setHasSociety] = useState(false);

  const fetchPage = useCallback(
    async (targetPage: number, mode: "initial" | "more") => {
      if (mode === "more") setLoadingMore(true);
      else setLoading(true);

      const societies = await GetUserSocietiesAPI();
      const societyIds = societies.map((society) => society.id);
      if (targetPage === 1) setHasSociety(societyIds.length > 0);

      const resp = await GetUploadedEventsAPI(societyIds, targetPage);
      if (mode === "more") setLoadingMore(false);
      setLoading(false);

      const result = resp?.result;
      if (resp?.status !== 0 || !Array.isArray(result)) {
        setError(true);
        setHasMore(false);
        return;
      }

      setError(false);
      setPage(targetPage);
      setHasMore(result.length >= PAGE_SIZE);
      setEvents((prev) => (mode === "more" ? [...prev, ...result] : result));
    },
    [],
  );

  useEffect(() => {
    fetchPage(1, "initial");
  }, [fetchPage]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{t("My Created Events")}</h1>
        <Link
          to="/student/events/new"
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#e08f0e]"
        >
          <Plus size={16} />
          {t("Create Event")}
        </Link>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {t("Events uploaded by your societies")}
      </p>

      <div className="mt-4">
        {loading ? (
          <AdminListSkeleton count={3} />
        ) : error ? (
          <div className="rounded-2xl border border-gray-100 bg-white">
            <AdminEmptyState
              iconType="MaterialCommunityIcons"
              iconName="cloud-alert-outline"
              title={t("Something went wrong")}
              message={t("Pull to refresh to load events.")}
              actionLabel={t("Retry")}
              onAction={() => fetchPage(1, "initial")}
            />
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white">
            <AdminEmptyState
              iconType="MaterialCommunityIcons"
              iconName="calendar-blank-outline"
              title={
                hasSociety ? t("No events yet") : t("No societies yet")
              }
              message={
                hasSociety
                  ? t("Events you upload will appear here.")
                  : t("Join a society to create an event")
              }
            />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {events.map((event) => {
                const { details } = event;
                const coverImages = toImageList(details.event_images);
                const dateLabel = formatDateRange(
                  details.start_date,
                  details.end_date,
                );
                const timeLabel = formatTimeRange(
                  details.start_time,
                  details.end_time,
                );
                return (
                  <article
                    key={details.id}
                    className="flex gap-3 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm"
                  >
                    {coverImages.length > 0 && (
                      <Link
                        to={`/student/events/${details.id}`}
                        state={{ event }}
                        className="shrink-0"
                      >
                        <img
                          src={coverImages[0]}
                          alt=""
                          loading="lazy"
                          className="h-24 w-24 rounded-xl object-cover"
                        />
                      </Link>
                    )}

                    <div className="flex min-w-0 flex-1 flex-col">
                      <StudentUniversityLogos
                        codes={toUniversityCodes(details.university)}
                      />
                      <Link
                        to={`/student/events/${details.id}`}
                        state={{ event }}
                      >
                        <h3 className="mt-1 line-clamp-1 text-[15px] font-bold text-gray-900">
                          {localized(
                            details.title,
                            details.title_en,
                            language,
                          ) || t("Untitled Event")}
                        </h3>
                      </Link>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                        {dateLabel && (
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays size={12} className="text-[#ffa217]" />
                            {dateLabel}
                          </span>
                        )}
                        {timeLabel && (
                          <span className="inline-flex items-center gap-1">
                            <Clock size={12} className="text-[#ffa217]" />
                            {timeLabel}
                          </span>
                        )}
                        {details.location && (
                          <span className="inline-flex min-w-0 items-center gap-1">
                            <MapPin size={12} className="shrink-0 text-[#ffa217]" />
                            <span className="truncate">{details.location}</span>
                          </span>
                        )}
                      </div>

                      <div className="mt-auto flex items-center justify-end pt-2">
                        <Link
                          to={`/student/events/${details.id}/edit`}
                          state={{ event }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
                        >
                          <Pencil size={13} />
                          {t("Edit")}
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {hasMore ? (
              <button
                type="button"
                onClick={() => fetchPage(page + 1, "more")}
                disabled={loadingMore}
                className="mt-4 w-full rounded-xl border border-[#ffa217] bg-white py-2.5 text-sm font-bold text-[#ffa217] transition hover:bg-[#fff8f0] disabled:opacity-60"
              >
                {loadingMore ? (
                  <Loader2 size={16} className="mx-auto animate-spin" />
                ) : (
                  t("Load more")
                )}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentMyEventsPage;
